// @flow
import React, {Component, Fragment} from 'react';
import {Helmet} from 'react-helmet-async';
import {compose} from 'redux';
import {connect} from 'react-redux';

import gql from 'graphql-tag';
import {Waypoint} from '@homeaway/react-unveil';
import Experiment from '@homeaway/react-experiment';
import {evaluateExperimentAndLog} from '@homeaway/abacus-client-utils';
import DatesPromptBanner from '../DatesPromptBanner/DatesPromptBanner';
import Hit, {fragments as hitFragments} from '../Hit/Hit';

import ErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import PinnedProperty from '../PinnedProperty/PinnedProperty';
import MultiUnitProperty from '../MultiUnitProperty/MultiUnitProperty';
import {trigger as edapTrigger, GENERIC_EVENT} from '../../utils/edapWrapper';
import ScrollListener from '../ScrollListener/ScrollListener';
import {isDateless} from '../../utils/searchParamsUtils';
import {isDifferent} from '../../utils';
import Timing from '../Timing/Timing';
import {XS_MIN} from '../../utils/breakpoints';
import {mergeListingsCollectionWithPinnedListing} from '../../utils/mergeListingsCollectionWithPinnedListing';

import type {ListingShape as HitListingShape} from '../Hit/Hit';
import withPAR from '../Timing/withPAR';
import {
    triggerListHitClickEDAPEvent,
    triggerMultiUnitHitClickEDAPEvent,
    triggerMultiUnitHitExposedEDAPEvent
} from '../../utils/hitEdapUtils';
import {
    HA_SERP_DATES_PROMPT,
    HA_MULTIUNIT_DECORATOR_TEST
} from '../../abacus/experiments';

import type {Breakpoint} from '../../utils/breakpoints';
import type {State as ReduxState} from '../../reducers';
import type {SearchParamsType} from '../../reducers/searchParams';

import './HitCollection.less';
import {
    hitThumbnailBackgroundImageClass,
    MAX_PRELOAD_HIT_THUMBNAIL
} from './hitCollectionUtils';
import {listingHasPrice} from '../../utils/priceUtils';

import {PETS_ALLOWED} from '../../constants/badges';

export const fragments = {
    results: gql`
        fragment HitCollectionResults on Results {
            fromRecord
            page
            pageSize
            listings {
                ...HitListing
            }
            pinnedListing {
                listing {
                    availableForDates
                    ...HitListing
                }
            }
        }
        ${hitFragments.listing}
    `
};

export type ListingShape = HitListingShape;

export type PinnedListing = {
    listing: ListingShape
};

type StateProps = {
    breakpoint: Breakpoint,
    currency?: string,
    directEntry: boolean,
    fetchImages?: string,
    fullLocale: string,
    guestsRequested: number,
    inputParams?: SearchParamsType,
    isDatelessSearch: boolean,
    isGoodBot: boolean,
    isMetric?: boolean,
    mock?: string,
    numFilters?: number,
    petsRequested: boolean,
    pinnedPropertyLabel?: string
};

type OwnProps = {
    results: {
        fromRecord?: number,
        page?: number,
        pageSize?: number,
        listings: ListingShape[],
        pinnedListing: ?PinnedListing
    },
    onScrollHitEnter: (number) => void,
    onHitMouseEnter: (string) => void,
    onHitMouseLeave: () => void
};

export type Props = OwnProps & StateProps;

export const checkPriceless = (listings: ListingShape[]) => {
    const numPriceless = listings.reduce(
        (acc, l) => acc + (listingHasPrice(l) ? 0 : 1),
        0
    );
    const pricelesslabel: string = numPriceless.toString();
    if (numPriceless > 0) {
        edapTrigger(GENERIC_EVENT, {
            eventaction: 'no price presented',
            highfrequency: 'true',
            eventlabel: pricelesslabel
        });
    }
};

export class HitCollection extends Component<Props> {
    constructor(props: Props) {
        super(props);
        const {
            results: {listings}
        } = props;
        checkPriceless(listings);
    }

    componentDidUpdate(prevProps: Props) {
        const {
            results: {listings: prevListings}
        } = prevProps;
        const {
            results: {listings}
        } = this.props;

        if (prevListings && listings && isDifferent(listings, prevListings)) {
            checkPriceless(listings);
        }
    }

    render() {
        const {
            directEntry,
            onHitMouseEnter,
            onHitMouseLeave,
            onScrollHitEnter,
            pinnedPropertyLabel,
            results: {
                fromRecord,
                page,
                pageSize,
                listings: listingsCollection,
                pinnedListing
            },
            breakpoint,
            guestsRequested,
            petsRequested,
            inputParams,
            currency,
            isMetric,
            isDatelessSearch,
            isGoodBot,
            fullLocale,
            mock
        } = this.props;
        let multiUnitPropertyInstrumentedForControl = false;
        let multiUnitPropertyInstrumentedForVariant = false;

        const listings = mergeListingsCollectionWithPinnedListing(
            listingsCollection,
            pinnedListing
        );

        const pinnedListingId = pinnedListing
            ? pinnedListing.listing.listingId
            : '';

        const handleEnter = (label: number) => {
            onScrollHitEnter(label);
        };

        const handleScroll = () => handleEnter(0);
        return (
            <Timing measureName="hitCollection">
                <ScrollListener
                    useWindowAsScrollParent
                    onScroll={handleScroll}
                    data-wdio="ScrollListener"
                >
                    <div
                        className="HitCollection"
                        data-wdio="HitCollection"
                        role="list"
                    >
                        {listings.map((listing, index) => {
                            const absoluteIndex = index + fromRecord - 1;
                            const hitProps = {
                                key: listing.listingId,
                                onHitClick: triggerListHitClickEDAPEvent,
                                index: absoluteIndex,
                                listing,
                                hitIndex: index,
                                page,
                                pageSize,
                                inputParams,
                                isGoodBot
                            };

                            if (listing.listingId === pinnedListingId) {
                                // SRS sets availability on dates only,
                                // this way we can construct a more refined message
                                const guestCountExceeded =
                                    guestsRequested > listing.sleeps;

                                const amenitiesBadges =
                                    listing.amenitiesBadges || [];

                                let petsNotAllowed =
                                    petsRequested &&
                                    !amenitiesBadges.find(
                                        (e) => e.id === PETS_ALLOWED
                                    );

                                const datesUnavailable = !listing.availableForDates;

                                return (
                                    <PinnedProperty
                                        datesUnavailable={datesUnavailable}
                                        guestCountExceeded={guestCountExceeded}
                                        key={index}
                                        label={pinnedPropertyLabel}
                                        numResults={listings.length}
                                        petsNotAllowed={petsNotAllowed}
                                    >
                                        <Hit
                                            {...hitProps}
                                            onMouseEnter={onHitMouseEnter}
                                            onMouseLeave={onHitMouseLeave}
                                        />
                                    </PinnedProperty>
                                );
                            }
                            if (listing.multiUnitProperty) {
                                const multiUnitProps = {
                                    listing,
                                    onMouseEnter: onHitMouseEnter,
                                    onMouseLeave: onHitMouseLeave,
                                    inputParams,
                                    breakpoint,
                                    listingNamespace: listing.listingNamespace,
                                    currency,
                                    isMetric,
                                    isDatelessSearch,
                                    fullLocale,
                                    mock
                                };

                                const handleMultiUnitHitExposed = () => {
                                    evaluateExperimentAndLog(
                                        HA_MULTIUNIT_DECORATOR_TEST
                                    );
                                    triggerMultiUnitHitExposedEDAPEvent(
                                        multiUnitProps
                                    );
                                };

                                const onHitClickMultiUnit = {
                                    onHitClick: triggerMultiUnitHitClickEDAPEvent
                                };
                                return (
                                    <Experiment
                                        name={HA_MULTIUNIT_DECORATOR_TEST}
                                        deferLogging
                                    >
                                        {({On, Off}) => (
                                            <React.Fragment>
                                                <On>
                                                    <ErrorBoundary>
                                                        {(!multiUnitPropertyInstrumentedForVariant && (
                                                            <Waypoint
                                                                onEnter={
                                                                    handleMultiUnitHitExposed
                                                                }
                                                                data-wdio={`Waypoint${index +
                                                                    1}`}
                                                                key={index}
                                                                label={
                                                                    index + 1
                                                                }
                                                                useWindowAsScrollParent
                                                            >
                                                                {
                                                                    (multiUnitPropertyInstrumentedForVariant = true)
                                                                }
                                                                <MultiUnitProperty
                                                                    key={index}
                                                                    {...multiUnitProps}
                                                                >
                                                                    <Hit
                                                                        {...hitProps}
                                                                        {...onHitClickMultiUnit}
                                                                    />
                                                                </MultiUnitProperty>
                                                            </Waypoint>
                                                        )) || (
                                                            <MultiUnitProperty
                                                                key={index}
                                                                {...multiUnitProps}
                                                            >
                                                                <Hit
                                                                    {...hitProps}
                                                                    {...onHitClickMultiUnit}
                                                                />
                                                            </MultiUnitProperty>
                                                        )}
                                                    </ErrorBoundary>
                                                </On>
                                                <Off>
                                                    {(!multiUnitPropertyInstrumentedForControl && (
                                                        <Waypoint
                                                            onEnter={
                                                                handleMultiUnitHitExposed
                                                            }
                                                            data-wdio={`Waypoint${index +
                                                                1}`}
                                                            key={index}
                                                            label={index + 1}
                                                            useWindowAsScrollParent
                                                        >
                                                            {
                                                                (multiUnitPropertyInstrumentedForControl = true)
                                                            }
                                                            <Hit
                                                                {...hitProps}
                                                                onMouseEnter={
                                                                    onHitMouseEnter
                                                                }
                                                                onMouseLeave={
                                                                    onHitMouseLeave
                                                                }
                                                            />
                                                        </Waypoint>
                                                    )) || (
                                                        <Hit
                                                            {...hitProps}
                                                            onMouseEnter={
                                                                onHitMouseEnter
                                                            }
                                                            onMouseLeave={
                                                                onHitMouseLeave
                                                            }
                                                        />
                                                    )}
                                                </Off>
                                            </React.Fragment>
                                        )}
                                    </Experiment>
                                );
                            }

                            if ([4, 9].includes(index)) {
                                return (
                                    <div data-wdio={`Waypoint${index + 1}`}>
                                        <Waypoint
                                            key={index}
                                            label={index + 1}
                                            onEnter={handleEnter}
                                            useWindowAsScrollParent
                                        >
                                            <Hit
                                                {...hitProps}
                                                onMouseEnter={onHitMouseEnter}
                                                onMouseLeave={onHitMouseLeave}
                                            />
                                        </Waypoint>
                                    </div>
                                );
                            }
                            if (index === 7) {
                                return (
                                    <Fragment key={index}>
                                        <Hit
                                            {...hitProps}
                                            onMouseEnter={onHitMouseEnter}
                                            onMouseLeave={onHitMouseLeave}
                                        />
                                        {isDatelessSearch && (
                                            <Experiment
                                                name={HA_SERP_DATES_PROMPT}
                                            >
                                                {({Variant}) => {
                                                    const banner = (
                                                        <DatesPromptBanner
                                                            useXsImage={
                                                                breakpoint ===
                                                                XS_MIN
                                                            }
                                                        />
                                                    );

                                                    return (
                                                        <Fragment>
                                                            <Variant
                                                                variant={1}
                                                            >
                                                                {banner}
                                                            </Variant>
                                                            <Variant
                                                                variant={2}
                                                            >
                                                                {!directEntry &&
                                                                    banner}
                                                            </Variant>
                                                        </Fragment>
                                                    );
                                                }}
                                            </Experiment>
                                        )}
                                    </Fragment>
                                );
                            }
                            return (
                                <Hit
                                    {...hitProps}
                                    onMouseEnter={onHitMouseEnter}
                                    onMouseLeave={onHitMouseLeave}
                                />
                            );
                        })}
                    </div>
                </ScrollListener>
            </Timing>
        );
    }
}

const mapStateToProps = ({
    viewport: {breakpoint},
    system: {currency, directEntry, fullLocale, isGoodBot, isMetric},
    searchParams: {params, numFilters, inputParams},
    pinnedProperty: {label},
    router: {query}
}: ReduxState): StateProps => {
    const {adultsCount = 0, childrenCount = 0, petIncluded = false} = params;
    const {mock} = query;

    return {
        breakpoint,
        currency,
        directEntry,
        fetchImages: params.fetchImages,
        fullLocale,
        guestsRequested: adultsCount + childrenCount,
        inputParams,
        isDatelessSearch: isDateless(params),
        isGoodBot,
        isMetric,
        mock,
        numFilters,
        petsRequested: petIncluded,
        pinnedPropertyLabel: label
    };
};

export const ConnectedHitCollection = compose(
    connect(mapStateToProps),
    withPAR
)(HitCollection);

const HitCollectionWithHelmet = (props: OwnProps) => {
    const {listings: listingsCollection, pinnedListing} = props.results;
    const listings = mergeListingsCollectionWithPinnedListing(
        listingsCollection,
        pinnedListing
    );

    return (
        <Fragment>
            {listings && (
                <Helmet>
                    <style>
                        {listings
                            .slice(0, MAX_PRELOAD_HIT_THUMBNAIL)
                            .filter(
                                (listing) =>
                                    !!(listing.images && listing.images[0])
                            )
                            .map((listing, index) => {
                                // Flow ignores check above
                                // $FlowFixMe
                                const url = listing.images[0].c6_uri;
                                return url
                                    ? `.${hitThumbnailBackgroundImageClass(
                                          index
                                      )} {background-image: url("${url}");}`
                                    : '';
                            })
                            .join(' ')}
                    </style>
                </Helmet>
            )}
            <ConnectedHitCollection {...props} />
        </Fragment>
    );
};

export default HitCollectionWithHelmet;
